import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    Typography,
    Paragraph,
    Colours,
} from '@ratehub/base-ui';

import QuoteDetails from './QuoteDetails';


function QuoteDetailsAuto({ vehicles, brokerName, brokerPhone, ...otherProps }) {
    const intl = useIntl();

    return (
        <>
            <For
                each="vehicle"
                of={vehicles}
            >
                <QuoteDetailsAuto.VehicleTitle
                    className={classNames('rh-title-3xs', 'rh-text-uppercase', 'weight-regular', 'rh-fg-blackberry', 'rh-mt-1', 'rh-mb-0', 'rh-mx-0')}
                >
                    {vehicle.fullName}
                </QuoteDetailsAuto.VehicleTitle>
                <QuoteDetails
                    key={vehicle.code}
                    coverageDetails={buildCoverageDetailsShape({
                        policyLength: vehicle.policyLength,
                        comprehensive: vehicle.comprehensive,
                        collision: vehicle.collision,
                        liability: vehicle.liability,
                    }, intl)}
                    endorsementDetails={buildEndorsementsShape({
                        lossOfUse: vehicle.lossOfUse,
                        accidentForgiveness: vehicle.accidentForgiveness,
                        removingDepreciation: vehicle.removingDepreciation,
                        rentalCarCoverage: vehicle.rentalCarCoverage,
                    }, intl)}
                    {...otherProps}
                />
            </For>
            <QuoteDetailsAuto.ContactInfo size="medium">
                {intl.formatMessage(Messages.SUPPORT, {
                    broker: brokerName,
                    contact: brokerPhone,
                })}
            </QuoteDetailsAuto.ContactInfo>
        </>
    );
}

QuoteDetailsAuto.propTypes = {
    /*  Assuming shape for now, may need to change */
    vehicles: PropTypes.arrayOf(
        PropTypes.shape({
            fullName: PropTypes.string.isRequired,
            code: PropTypes.string.isRequired,
            policyLength: PropTypes.number.isRequired,
            comprehensive: PropTypes.number.isRequired,
            collision: PropTypes.number.isRequired,
            liability: PropTypes.number.isRequired,
            lossOfUse: PropTypes.bool.isRequired,
            accidentForgiveness: PropTypes.bool.isRequired,
            removingDepreciation: PropTypes.bool.isRequired,
            rentalCarCoverage: PropTypes.bool.isRequired,
        }),
    ).isRequired,
    brokerName: PropTypes.string.isRequired,
    brokerPhone: PropTypes.string.isRequired,
};

/* This function formats our coverage object into the shape needed
    for QuoteDetails since that component is re-used between Home + Auto.
    Additionally, formatting strings needs to be done here since some items
    use formatNumber & have unique messages.  */
function buildCoverageDetailsShape(coverage, intl) {
    return [
        {
            label: intl.formatMessage(Messages.POLICY_LENGTH),
            value: intl.formatMessage(Messages.MONTHS, { amount: coverage.policyLength }),
        },
        {
            label: intl.formatMessage(Messages.COMPREHENSIVE),
            value: intl.formatNumber(coverage.comprehensive, {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        },
        {
            label: intl.formatMessage(Messages.COLLISION),
            value: intl.formatNumber(coverage.collision, {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        },
        {
            label: intl.formatMessage(Messages.LIABILITY),
            value: intl.formatNumber(coverage.liability, {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        },
    ];
}

/* This function formats our endorsements object into the shape needed
    for QuoteDetails since that component is re-used between Home + Auto.
    Additionally, formatting strings needs to be done here since some items
    use formatNumber & have unique messages.  */
function buildEndorsementsShape(endorsements, intl) {
    return [
        {
            label: intl.formatMessage(Messages.LOSS_OF_USE),
            value: endorsements.lossOfUse,
        },
        {
            label: intl.formatMessage(Messages.ACCIDENT_FORGIVENESS),
            value: endorsements.accidentForgiveness,
        },
        {
            label: intl.formatMessage(Messages.REMOVING_DEPRECIATION),
            value: endorsements.removingDepreciation,
        },
        {
            label: intl.formatMessage(Messages.RENTAL_CAR_COVERAGE),
            value: endorsements.rentalCarCoverage,
        },
    ];
}

const Messages = defineMessages({
    POLICY_LENGTH: {
        id: 'Accounts.autoQuoteDetails.policyLength',
        defaultMessage: 'Policy length',
    },
    MONTHS: {
        id: 'Accounts.autoQuoteDetails.months',
        defaultMessage: '{amount} months',
    },
    PACKAGE: {
        id: 'Accounts.autoQuoteDetails.coveragePackage',
        defaultMessage: 'Coverage package',
    },
    COMPREHENSIVE: {
        id: 'Accounts.autoQuoteDetails.comprehensive',
        defaultMessage: 'Comprehensive deductible',
    },
    COLLISION: {
        id: 'Accounts.autoQuoteDetails.collision',
        defaultMessage: 'Collision deductible',
    },
    LIABILITY: {
        id: 'Accounts.autoQuoteDetails.liability',
        defaultMessage: 'Liability',
    },
    LOSS_OF_USE: {
        id: 'Accounts.autoQuoteDetails.lossOfUse',
        defaultMessage: 'Loss of use',
    },
    ACCIDENT_FORGIVENESS: {
        id: 'Accounts.autoQuoteDetails.accidentWaiver',
        defaultMessage: 'Accident waiver',
    },
    REMOVING_DEPRECIATION: {
        id: 'Accounts.autoQuoteDetails.removingDepreciation',
        defaultMessage: 'Removing depreciation deduction',
    },
    RENTAL_CAR_COVERAGE: {
        id: 'Accounts.autoQuoteDetails.rentalCarCoverage',
        defaultMessage: 'Liability for damage for non-owned automobiles',
    },
    SUPPORT: {
        id: 'Accounts.autoQuoteDetails.brokerSupport',
        defaultMessage: '{broker} support: {contact}',
    },
});


QuoteDetailsAuto.VehicleTitle = styled.p`
    color: ${Colours.STONE_DARKEST};
`;

QuoteDetailsAuto.ContactInfo = styled(Paragraph)`
    font-weight: ${Typography.WEIGHTS.MEDIUM};
`;

export default QuoteDetailsAuto;
